// import Vue from 'vue';
import axios from 'axios';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import _unset from 'lodash/unset';
import qs from 'qs';

import router from '@/router';
import { constants } from '@/lib/constants';
import useWorkstationCookie from '@/hooks/common/useWorkstationCookie';
import useSmartTerminalCookie from '@/hooks/common/useSmartTerminalCookie';
import { openReportOutputs, post } from '@/lib/utils';
import {
  PasswordResetException,
  AuthenticationException,
  APIException,
} from '@/lib/exceptions';


const { getWorkstationCookie } = useWorkstationCookie();
const { getSmartTerminalCookie, setSmartTerminalCookie, updateSmartTerminalCookie } = useSmartTerminalCookie();

const localState = {
  authenticated: false,
  auth: {},
  loginSites: [],
  previous_username: null,
  username: null,
  loggedInSiteId: null,
  loggedInSite: {},
  userSiteIds: {},
  entryRoute: null,
  token: null,
  token_expiry: 0,
  timeoutId: null,
  activityId: null,
  enabledCashDrawers: [],
  cashDrawer: {},
  cashDrawerId: null,
  cardReader: null,
  chipReader: null,
  dialogDevice: null,
  smartTerminals: [],
  selectedSmartTerminal: null,
  checkScannersEnabled: false,
  cardReadersEnabled: false,
  chipReadersEnabled: false,
  netReadersEnabled: false,
  paymentGateway: null,
  showDeviceDialogModal: false,
  showStoreCardModal: false,
  showInstallmentCalculatorModal: false,
  deviceDialogModel: {},
  loftNodeVersion: null,
};


const localGetters = {
  authenticated: state => {
    return state.authenticated;
  },

  auth: state => {
    return state.auth;
  },

  token: state => {
    return state.token;
  },

  token_expiry: state => {
    return state.token_expiry;
  },

  username: state => {
    return state.username;
  },

  displayname: state => {
    return _get(state, 'auth.displayname', '');
  },

  useralias: state => {
    return state.auth.useralias;
  },

  loggedInSiteId: state => {
    return state.loggedInSiteId || _get(getWorkstationCookie(), 'siteid');
  },

  loggedInSite: state => {
    return state.loggedInSite;
  },

  getUserLoginInfo: state => username => {
    return _get(state, `userSiteIds.${username}`, {});
  },

  loginSites: state => {
    return state.loginSites;
  },

  visibleSites: state => {
    return _get(state, 'auth.visiblesites', state.loginSites);
  },

  /**
   * Determine if the user is on a desktop via checking if it's mobile
   * @returns {Boolean}
   */
  isMobile: () => !!navigator.userAgent.match(/(iPad)|(iPhone)|(iPod)|(android)|(webOS)/i),

  isTouchScreen: () => window.matchMedia('(pointer: coarse)').matches,

  entryRoute: state => {
    return state.entryRoute;
  },

  enabledCashDrawers: state => {
    return state.enabledCashDrawers;
  },

  cashDrawer: state => {
    if (!_isEmpty(state.cashDrawer)) return state.cashDrawer;
    const cashdrawerid = _get(getWorkstationCookie(), 'cashdrawerid');
    if (cashdrawerid) {
      if (state.enabledCashDrawers.length) {
        return state.enabledCashDrawers.find(cd => cd.cashdrawerid === cashdrawerid);
      }
      return { cashdrawerid };
    }
    return {};
  },

  permissions: state => name => {
    return state.auth.permissions.find(p => p.permissionname === name);
  },

  isValidTerminalId: (state, getters, rootState, rootGetters) => smartTerminalId => {
    const smartTerminals = rootGetters['cached/get_provider_data']('smartterminals');
    const data = _get(smartTerminals, 'data');
    return !!(data && data.find(d => d.smartTerminalId === smartTerminalId));
  },

  selectedSmartTerminal: (state, getters, rootState, rootGetters) => {
    const terminal = state.selectedSmartTerminal || getSmartTerminalCookie();
    return rootGetters['auth/isValidTerminalId'](terminal) ? terminal : null;
  },

  cardReader: state => state.cardReader,
  chipReader: state => state.chipReader,
  dialogDevice: state => state.dialogDevice,
  cardReadersEnabled: state => state.cardReadersEnabled,
  chipReadersEnabled: state => state.chipReadersEnabled,
  netReadersEnabled: state => state.netReadersEnabled,
  paymentGateway: state => state.paymentGateway,
  showDeviceDialogModal: state => state.showDeviceDialogModal,
  deviceDialogModel: state => state.deviceDialogModel,
  loftNodeVersion: state => state.loftNodeVersion,
  checkScannersEnabled: state => state.checkScannersEnabled,
  showStoreCardModal: state => state.showStoreCardModal,
  showInstallmentCalculatorModal: state => state.showInstallmentCalculatorModal,
  timeoutId: state => state.timeoutId,
  previousUsername: state => state.previous_username,
  shouldSaveSiteAndDrawer: (state, getters, rootState, rootGetters) => {
    return rootGetters['meta/systemParameter']('save_site_cashdrawer_on_login') === 'Y';
  },
};


const localMutations = {

  login(state, data) {
    const { DB_TRUE } = constants;
    const auth_obj = data.user;
    const login_obj = data.login_obj;
    const { site, siteid, cashdrawer } = login_obj;
    state.auth = auth_obj;
    state.sso = login_obj.ssologin;
    state.loggedInSiteId = siteid;
    state.loggedInSite = site;
    state.userSiteIds[login_obj.username] = { siteid, cashdrawer };
    state.username = auth_obj.username;
    state.previous_username = login_obj.username;
    state.cashDrawer = cashdrawer;
    state.cashDrawerId = cashdrawer.cashdrawerid;
    state.enabledCashDrawers = login_obj.enabledcashdrawers;
    state.checkScannersEnabled = _get(state, 'loggedInSite.checkscannersenabled', false) === DB_TRUE;
    state.cardReadersEnabled = _get(state, 'loggedInSite.cardreadersenabled', false) === DB_TRUE;
    state.chipReadersEnabled = _get(state, 'loggedInSite.chipreadersenabled', false) === DB_TRUE;
    state.paymentGateway = _get(state, 'loggedInSite.paymentgateway');
    state.netReadersEnabled = state.paymentGateway === 'payjunction' && state.chipReadersEnabled;
  },

  authenticated(state, bool) {
    if (bool === undefined) {
      state.authenticated = !state.authenticated;
    }
    else {
      state.authenticated = bool;
    }
  },

  loginSites(state, sites) {
    state.loginSites = sites;
  },

  logout(state) {
    state.authenticated = false;
    state.auth = {};
    state.username = null;
  },

  disassociate(state) {
    // Delete cookie and clear previously logged in site/cash drawer (e.g. "Disassociate Workstation...")
    state.loggedInSiteId = null;
    state.loggedInSite = {};
    state.cashDrawer = {};
    state.cashDrawerId = null;
  },

  updateEntryRoute(state, route) {
    state.entryRoute = { name: route.name, path: route.path, params: route.params, meta: route.meta, query: route.query };
  },

  deleteEntryRoute(state) {
    state.entryRoute = null;
  },

  update_token(state, token_data) {
    state.token = token_data.token;
    state.token_expiry = token_data.token_expiry;
  },

  updateTimeoutId(state, id) {
    state.timeoutId = id;
  },

  updateActivityId(state, id) {
    state.activityId = id;
  },

  set_enabled_cash_drawers(state, { enabledcashdrawers }) {
    state.enabledCashDrawers = enabledcashdrawers;
  },

  set_cash_drawer(state, cashDrawer) {
    state.cashDrawer = cashDrawer;
  },

  set_loftnode_version(state, result) {
    if (result && result.ln_version) {
      state.loftNodeVersion = result.ln_version;
    }
  },

  set_smart_terminal(state, smartTerminalId) {
    const id = getSmartTerminalCookie();
    if (id) updateSmartTerminalCookie(smartTerminalId);
    else setSmartTerminalCookie(smartTerminalId);
    state.selectedSmartTerminal = smartTerminalId;
  },

  set_local_devices(state, devices) {
    let _cardReader = null;
    let _chipReader = null;
    let _dialogDevice = null;
    // NOTE: we only support one local (i.e. MagTek device)
    // if more than one is connected the first one is used
    // LoftNode already sorts the devices by putting readers in
    // EMV/Chip > Pin > Mag-stripe order
    const items = devices.map((item) => {
      item.label = `${item.manufacturer || ''} - ${item.product}`.trim();
      _unset(item, 'path');
      const prd = item.product.toUpperCase();
      if (item.vendorId === 2049 && item.productId >= 12292) {
        if (prd.indexOf('SC') >= 0 || prd.indexOf('GO') >= 0) {
          // signature capable devices
          _dialogDevice = item;
        }
        if (prd.indexOf('DYNAPRO') >= 0) {
          // all EMV capable device
          // does not guarantee NFC capable
          _chipReader = item;
        }
      }
      return item;
    });
    _cardReader = items[0];
    state.cardReader = _cardReader;
    state.chipReader = _chipReader;
    state.dialogDevice = _dialogDevice;
  },

  toggle_device_dialog_modal(state, payload) {
    const { requestType,
      requestData,
      deviceConfig,
      immediate,
      async_params,
      smartTerminalId,
      showModal = false } = payload;
    state.deviceDialogModel = { requestData, deviceConfig, requestType, async_params, immediate, smartTerminalId };
    state.showDeviceDialogModal = showModal;
  },

  reset_auth(state, shouldClearToken = true) {
    const s = localState;
    for (const key of Object.keys(s)) {
      // state.loginSites is set by `meta/getClientSetupData`
      // and should not be cleared out changed login user.
      if (!shouldClearToken && ['token', 'token_expiry', 'timeoutId'].includes(key)) continue;
      if (['loginSites', 'loggedInSiteId', 'cashDrawer', 'cashDrawerId', 'userSiteIds'].includes(key)) continue;
      state[key] = s[key];
    }
  },

  toggle_storecard_dialog_modal(state) {
    state.showStoreCardModal = !state.showStoreCardModal;
  },

  toggle_installment_calculator_modal(state) {
    state.showInstallmentCalculatorModal = !state.showInstallmentCalculatorModal;
  },

  set_workstation_data(state, { siteid, cashdrawerid }) {
    state.loggedInSiteId = siteid;
    state.cashDrawerId = cashdrawerid;
  },
};


const localActions = {

  rememberEntryRoute({ commit }, to) {
    commit('updateEntryRoute', to);
  },

  forgetEntryRoute({ commit }) {
    commit('deleteEntryRoute');
  },

  // eslint-disable-next-line max-lines-per-function
  async authenticate({ commit, dispatch, state, rootGetters }, login_obj) {
    const {
      username,
      password,
      site,
      cashdrawer,
      enabledcashdrawers,
      resetpassword = null,
      newpassword = null,
      newpasswordverify = null,
      workstationsetup = false,
    } = login_obj;
    const loginParams = {
      siteid: site.siteid,
      resetpassword,
      newpassword,
      newpasswordverify,
      workstation_setup: workstationsetup,
    };
    if (login_obj.ssoLogin) {
      // This is a secondary safety net mechanism to make sure
      // we log in whom we think we are.
      // (Note case difference)
      loginParams.ssologin = login_obj.ssoLogin;
    }
    try {
      const authQry = await axios({
        method: 'post',
        url: '/tg/authenticate',
        params: loginParams,
        auth: {
          username,
          password,
        },
      });
      if (authQry.data.response.errors) {
        if (authQry.data.response.log_out_sso) dispatch('logoutSSO');
        if (authQry.data.response.password_reset) {
          throw new PasswordResetException('Password reset successful', true);
        }
        throw new AuthenticationException();
      }
      const user = authQry.data.response.results[0];
      const reset = user.resetpassword === 'Y' || loginParams.resetpassword === 'Y';
      if (reset && !login_obj.ssoLogin) throw new PasswordResetException('Reset your password');
      if (state.previous_username && username !== state.previous_username) await dispatch('resetAuth', false);
      if (!rootGetters['meta/clientSetupData']) await this.dispatch('meta/getClientSetupData');
      commit('login', {
        user,
        login_obj: {
          ...loginParams,
          site,
          username,
          cashdrawer,
          enabledcashdrawers,
        },
      });
      if (rootGetters['auth/shouldSaveSiteAndDrawer']) {
        const cookieData = { siteid: site.siteid, cashdrawerid: cashdrawer.cashdrawerid };
        const { setWorkstationCookie, updateWorkstationCookie } = useWorkstationCookie();
        if (login_obj.workstation_setup) setWorkstationCookie(cookieData);
        else updateWorkstationCookie(cookieData);
      }
      if (rootGetters['notify/logoutMessageId']) {
        // TODO: Fix once Vue notifications are back up and running
        // Vue.notify.close(rootGetters['notify/logoutMessageId']);
        this.dispatch('notify/setLogoutId', null, { root: true });
      }
      await this.dispatch('meta/getMetadata');
      await this.dispatch('preferences/initializePreferences', user);
      this.dispatch('meta/initializeUqlConstants');
      this.dispatch('cached/initializeVisibleSites', state.loginSites);
      this.dispatch('cached/clearUserCache');
      this.dispatch('permissions/initializePermissions', user.permissions);
      commit('authenticated', true);
      await dispatch('deviceIntraface', { requestType: 'getDevices' });
    }
    catch (e) {
      if (e instanceof APIException) {
        throw new AuthenticationException(e.notifyParams);
      }
      throw e;
    }
  },

  logoutSSO() {
    // Open a popup:
    // We need to hit Apache Mellon and then redirect it to the "close yourself"
    window.open(
      '/mellon/logout?ReturnTo=/tg/close_popupwindow',
      'Company Logout',
      'width=500,height=600',
    );
  },

  async refreshToken({ dispatch }) {
    const refresh = await axios({ method: 'post', url: '/tg/refreshtoken' });
    const token = refresh.headers['x-auth-token'];
    const token_expiry = refresh.headers['x-auth-expiry'];
    if (!token || !token_expiry) {
      dispatch('logout');
    }
  },

  updateToken({ commit, dispatch }, { token, token_expiry }) {
    commit('update_token', { token, token_expiry });
    dispatch('refresh');
  },

  refresh({ commit, state, dispatch }) {
    // timeout at 90% of expiry so we aren't burnt by bad timer
    const timeoutInMs = state.token_expiry * 900;
    if (!Number.isNaN(timeoutInMs)) {
      dispatch('stopRefresh');
      const id = window.setTimeout(() => dispatch('refreshToken'), timeoutInMs);
      commit('updateTimeoutId', id);
    }
    else {
      dispatch('logout');
    }
  },

  stopRefresh({ commit, state }) {
    if (state.timeoutId !== null) {
      window.clearTimeout(state.timeoutId);
      commit('updateTimeoutId', null);
    }
  },

  logout({ rootGetters, commit, state, dispatch }, payload = {}) {
    const {
      shouldDisassociate = false,
      rememberEntryRoute = true,
      manual = false,
    } = payload;
    commit('logout');
    if (shouldDisassociate) commit('disassociate');
    if (!rootGetters['auth/shouldSaveSiteAndDrawer'] || shouldDisassociate) {
      const { deleteWorkstationCookie } = useWorkstationCookie();
      deleteWorkstationCookie();
    }
    if (manual && state.sso) dispatch('logoutSSO');
    dispatch('stopRefresh');
    if (rememberEntryRoute) dispatch('rememberEntryRoute', router.currentRoute.value);
    dispatch('cached/clearDataProviders', null, { root: true });
    dispatch('glAccount/clearStaleGlAccounts', {}, { root: true });
    router.push({ name: 'login' });
  },

  setCashDrawer({ commit, dispatch }, cashDrawer) {
    commit('set_cash_drawer', cashDrawer);
    dispatch('notify/push', {
      group: 'default',
      duration: 3000,
      title: 'Cash Drawer Changed',
      text: `Cash Drawer successfully changed to "${cashDrawer.cashdrawerid}"`,
      type: 'success',
    }, { root: true });
  },

  async resetAuth({ commit }, shouldClearToken = true) {
    this.dispatch('cached/invalidateScreenData'); // clear previously visited screens
    this.dispatch('cached/clearUserCache'); // clear search cache
    this.dispatch('advancedSearch/clearLocalState'); // clear advanced searches
    commit('reset_auth', shouldClearToken);
  },

  setSmartTerminal({ commit }, smartTerminalId) {
    commit('set_smart_terminal', smartTerminalId);
  },

  // eslint-disable-next-line max-lines-per-function
  async deviceIntraface({ commit, dispatch, state }, dialog_obj) {
    const {
      requestData = {},
      deviceConfig = {},
      requestType = 'getDevices',
      resultAction = null,
    } = dialog_obj;
    deviceConfig.companyid = _get(state, 'loggedInSite.companyid');
    deviceConfig.siteid = _get(state, 'loggedInSite.siteid');
    deviceConfig.cashdrawerid = _get(state, 'cashDrawer.cashdrawerid');
    deviceConfig.quickChip = true;
    // when you load/refresh loft-ui getDevices is called
    // which sets the `set_loftnode_version` in state.
    // if we get any `getEMV` calls we should have the version
    // if it is NOT there, that means they are running a really old
    // LoftNode pre 2.5 - treat it like an error.
    if (requestType === 'get' && (!state.loftNodeVersion ||
        state.loftNodeVersion.split('.').shift() < '3')) {
      this.dispatch('notify/push', {
        title: `Unsupported version of LoftNode detected ${state.loftNodeVersion}. Upgrade required!`,
      });
      return {};
    }
    const reqParams = {
      CardRead: JSON.stringify(requestData),
      DeviceConfig: JSON.stringify(deviceConfig),
    };
    const loftNodeCall = await axios({
      method: 'post',
      url: `https://local.retailarchitects.com:8900/${requestType}`,
      data: qs.stringify(reqParams),
      headers: {
        'content-type': 'application/x-www-form-urlencoded;charset=utf-8',
      },
    });
    const response = _get(loftNodeCall, 'data.response');
    if (response) {
      if (resultAction) {
        dispatch(resultAction, response);
      }
      if (requestType === 'getDevices') {
        commit('set_local_devices', response.results);
        commit('set_loftnode_version', response);
      }
      else if (response.results.length) {
        const deviceResponse = response.results[0];
        // we only support LoftNode 3.x so, no talking
        // to the payment gateway from LoftNode.
        // 'clientsideprocessing' no longer used?
        deviceResponse.clientsideprocessing = 'N';
        deviceResponse.deviceid = _get(requestData, 'selectedCardReader.serialNumber', _get(state, 'cardReader.serialNumber'));
        return deviceResponse;
      }
      return response;
    }
    if (requestType !== 'getDevices') {
      this.dispatch('notify/push', { title: 'Could not reach LoftNode!' });
    }
    return {};
  },

  toggleDeviceDialogModal({ commit }, payload) {
    commit('toggle_device_dialog_modal', payload);
  },

  // eslint-disable-next-line max-lines-per-function
  processAsyncRequests({ dispatch, state }, response) {
    const queue = [...response.asyncrequests];
    // break down the queue
    const asyncOrderTrans = [];
    const asyncReportReqs = [];
    // eslint-disable-next-line no-unused-vars
    let mainorderid = '';
    // eslint-disable-next-line no-unused-vars
    let asyncQueueArgs = null;
    // eslint-disable-next-line no-unused-vars
    let asyncStatusArgs = null;
    // eslint-disable-next-line no-unused-vars
    const asyncPromptArgs = null;
    for (let i = 0, len = queue.length; i < len; i += 1) {
      const req = queue[i];
      if (req.type === 'report') {
        asyncReportReqs.push(req);
      }
      else if (req.type === 'asyncthirdpartyfinance' ||
        req.type === 'asyncordercreditcardgateway' ||
        req.type === 'asyncecacheck') {
        // ASYNC_PAYMENTS
        mainorderid = req.asyncparams.mainorderid;
        asyncOrderTrans.push(req.asyncparams);
      }
      else if (req.type === 'asyncqueue') {
        asyncQueueArgs = req.asyncparams;
      }
      else if (req.type === 'asyncstatus') {
        asyncStatusArgs = req;
      }
      else if (req.type === 'asyncprompt') {
        // SmarTerminal/PJ prompts are user facing
        // the prompt status is handled in DeviceDialogModal
        continue;
      }
    }
    const params = {};
    // now fire off in the correct order.
    if (asyncStatusArgs) {
      // requestType = 'asyncstatus'
      params.requestType = asyncStatusArgs.type;
      params.showModal = true;
      params.requestid = asyncStatusArgs.requestid;
      params.async_params = asyncStatusArgs;
    }
    else if (asyncPromptArgs) {
      // requestType = 'asyncprompt'
      params.requestType = asyncPromptArgs.type;
      params.showModal = true;
      params.requestid = asyncPromptArgs.requestid;
      params.async_params = asyncPromptArgs;
    }
    else if (asyncQueueArgs) {
      // requestType = 'asyncqueue'
      params.requestType = 'asyncqueue';
      params.showModal = true;
      params.async_params = asyncQueueArgs;
    }
    else if (asyncOrderTrans.length) {
      // requestType = 'asyncordertrans'
      /* Multiple types are handled here.
      req.type === 'asyncthirdpartyfinance'
      req.type === 'asyncordercreditcardgateway'
      req.type === 'asyncecacheck'
      */
      params.requestType = 'asyncordertrans';
      params.showModal = true;
      params.async_params = {
        trans: asyncOrderTrans,
        reports: asyncReportReqs,
        mainorderid,
      };
    }
    else if (asyncReportReqs.length) {
      asyncReportReqs.forEach(async (repreq) => {
        const asyncType = repreq.dialogprompts ? 'asyncRequestDialog' : 'asyncRequestReport';
        const isDialog = asyncType === 'asyncRequestDialog';
        const reportParams = {
          requestType: asyncType,
          showModal: isDialog,
          async_params: repreq,
        };
        if (isDialog) dispatch('toggleDeviceDialogModal', reportParams);
        else {
          if (state.netReadersEnabled || state.dialogDevice) {
            repreq.dialogdevicepresent = true;
          }
          const { results, response: payload } = await post('/tg/report/runreport', repreq, true);
          openReportOutputs(results, payload);
        }
      });
    }
    if (!_isEmpty(params)) {
      // launch the device progress modal
      dispatch('toggleDeviceDialogModal', params);
    }
  },

  toggleStoreCardModal({ commit }) {
    commit('toggle_storecard_dialog_modal');
  },

  toggleInstallmentCalculatorModal({ commit }) {
    commit('toggle_installment_calculator_modal');
  },

  setWorkstationData({ commit }, payload) {
    commit('set_workstation_data', payload);
  },
};


export default {
  namespaced: true,
  state: localState,
  getters: localGetters,
  actions: localActions,
  mutations: localMutations,
};
